import React, { useEffect, useState } from 'react'
import AppRating from './AppRating'
import { useUserSettings, useIsWebviewMode } from 'src/Hooks'
import { rateAppFromReactNative, sendEmailFromReactNative } from '@e3dc-react/shell/Libs/react-native/send-message-to-react-native'
import {
  ANDROID_APP_STORE_LINK,
  APP_FEEDBACK_EMAIL_ADDRESS,
  APP_FEEDBACK_EMAIL_HREF,
  APP_FEEDBACK_EMAIL_SUBJECT,
  IOS_APP_STORE_LINK,
  RATE_APP_CRITERIA_MET,
  REMIND_ME_LATER_AFTER_BUTTON_CLICK_WEEKS_AMOUNT,
  REMIND_ME_LATER_AFTER_CLOSE_MONTHS_AMOUNT,
  REMIND_ME_LATER_AFTER_RATING_MONTHS_AMOUNT,
} from '../consts'
import { useAppRatingContext, useSystemLogError } from '../hooks'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { APP_VERSION_UPDATE_DATE_KEY } from '@e3dc-react/shell/Libs/consts'
import { date } from '@e3dc-react/shell/Libs/date'
import { AppRatingFeedbackKeys } from '../types'
import { checkIsRemindMeLaterDateReached, checkIsPortalUpdateOldEnough } from '../utils'
import { saveAppRatingFeedbackValue } from '../api'
import { useTypedSelector } from 'src/State/RootReducer'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { isAndroid, isIOS } from '@e3dc-react/shell/Configuration'
import { useMediaQuery } from '@mui/system'
import { Theme } from '@mui/material'

/**
 * App Rating container providing all the needed data for the display component
 */
const AppRatingContainer: React.FC = () => {
  const { userSettings, updateUserSettings } = useUserSettings()
  const doesMeetAppRateCriteria = JSON.parse(localStorage.getItem(RATE_APP_CRITERIA_MET) ?? 'false')
  const { openAppRatingTriggered, resetAppRatingTrigger, appRatingOpened, closeAppRating } = useAppRatingContext()
  const { featureFlags, isFeatureFlagsLoading } = useFeatureFlagsContext()
  const portalUpdateDate = localStorage.getItem(APP_VERSION_UPDATE_DATE_KEY) ?? undefined
  const [open, setOpen] = useState(false)
  const [emailFeedbackContentActive, setEmailFeedbackContentActive] = useState(false)
  const userId = useTypedSelector(state => state.auth.userId)
  const authFetch = useAuthFetchWithResponse()
  const isWebview = useIsWebviewMode()
  const downMd = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const { remindMeLaterDate } = userSettings

  const meetsRemindMeLaterDateCriteria = remindMeLaterDate ? checkIsRemindMeLaterDateReached(remindMeLaterDate) : true
  const isPortalUpdateOldEnough = checkIsPortalUpdateOldEnough(portalUpdateDate)
  const featureEnabled = !isFeatureFlagsLoading && featureFlags.isAppRatingEnabled
  const doesMeetCriteria =
    featureEnabled && doesMeetAppRateCriteria && openAppRatingTriggered && isPortalUpdateOldEnough && meetsRemindMeLaterDateCriteria

  const { hasErrorLogs } = useSystemLogError(doesMeetCriteria)
  const appRateAvailable = (doesMeetCriteria && !hasErrorLogs) || appRatingOpened

  useEffect(() => {
    if (!appRateAvailable) {
      setOpen(false)
      return
    }

    setOpen(true)

    /* reset on unmount */
    return () => {
      setOpen(false)
      setEmailFeedbackContentActive(false)
    }
  }, [appRateAvailable])

  const handleRemindMeLater = (): void => {
    resetAppRatingTrigger()
    closeAppRating()
    updateUserSettings({
      remindMeLaterDate: date.addWeeks(new Date(), REMIND_ME_LATER_AFTER_BUTTON_CLICK_WEEKS_AMOUNT).toISOString(),
    })
    saveAppRatingFeedbackValue(userId, AppRatingFeedbackKeys.REMIND_ME_LATER, authFetch)
    setOpen(false)
  }

  const handleSendEmail = (): void => {
    resetAppRatingTrigger()
    closeAppRating()

    if (isWebview) {
      sendEmailFromReactNative({ body: '', recipient: APP_FEEDBACK_EMAIL_ADDRESS, subject: APP_FEEDBACK_EMAIL_SUBJECT })
    } else {
      window.open(APP_FEEDBACK_EMAIL_HREF, '_blank', 'noopener,noreferrer')
    }

    updateUserSettings({ remindMeLaterDate: date.addMonths(new Date(), REMIND_ME_LATER_AFTER_RATING_MONTHS_AMOUNT).toISOString() })
    setOpen(false)
  }

  const handleRateApp = (): void => {
    /* navigate the user to the app store if the rating was triggered from browser and not from the app */
    if (!isWebview) {
      if (isAndroid) {
        window.open(ANDROID_APP_STORE_LINK, '_blank', 'noopener,noreferrer')
      }
      if (isIOS) {
        window.open(IOS_APP_STORE_LINK, '_blank', 'noopener,noreferrer')
      }
      resetAppRatingTrigger()
      closeAppRating()
      setOpen(false)
      return
    }

    resetAppRatingTrigger()
    closeAppRating()
    rateAppFromReactNative()
    updateUserSettings({
      remindMeLaterDate: date.addMonths(new Date(), REMIND_ME_LATER_AFTER_RATING_MONTHS_AMOUNT).toISOString(),
    })
    saveAppRatingFeedbackValue(userId, AppRatingFeedbackKeys.YES, authFetch)
    setOpen(false)
  }

  const handleClose = (): void => {
    resetAppRatingTrigger()
    closeAppRating()
    updateUserSettings({
      remindMeLaterDate: date.addMonths(new Date(), REMIND_ME_LATER_AFTER_CLOSE_MONTHS_AMOUNT).toISOString(),
    })
    if (!emailFeedbackContentActive) {
      saveAppRatingFeedbackValue(userId, AppRatingFeedbackKeys.IGNORE, authFetch)
    }
    setOpen(false)
  }

  const handleDislikeApp = (): void => {
    saveAppRatingFeedbackValue(userId, AppRatingFeedbackKeys.NO, authFetch)
    setEmailFeedbackContentActive(true)
  }

  if (downMd) {
    return (
      <AppRating
        handleRemindMeLater={handleRemindMeLater}
        handleSendEmail={handleSendEmail}
        handleRateApp={handleRateApp}
        handleClose={handleClose}
        handleDislikeApp={handleDislikeApp}
        open={open}
        emailFeedbackContentActive={emailFeedbackContentActive}
      />
    )
  }

  return null
}

export default AppRatingContainer
